import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Refresh,
  TrendingUp,
  People,
  Visibility,
  CheckCircle,
  AttachMoney,
  Download,
  FilterList,
  Search,
} from '@mui/icons-material';
import { getReferralStats } from './referralUtils';
import { formatDate, formatDiscountAmount } from './referralUtils';
import { MESSAGES, FILTER_OPTIONS } from './referralConfig';

const AdminReferralStats = ({ isChampions = false }) => {
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    loadStats();
  }, [period]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (isChampions) {
        setStats([]);
        setError(null);
      } else {
        // For admins, show all stats
        const data = await getReferralStats(period);
        setStats(data.stats || []);
      }
    } catch (err) {
      setError('Failed to load referral statistics');
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredStats = stats.filter(stat => {
    const matchesSearch = stat.code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || stat.code_type === filterType;
    return matchesSearch && matchesType;
  });

  const getTotalStats = () => {
    return filteredStats.reduce((acc, stat) => ({
      totalClicks: acc.totalClicks + stat.clicks,
      totalUniqueClicks: acc.totalUniqueClicks + stat.unique_clicks,
      totalRegistrations: acc.totalRegistrations + stat.registrations,
      totalRedemptions: acc.totalRedemptions + stat.redemptions,
      totalUniqueUsers: acc.totalUniqueUsers + stat.unique_users,
      totalDiscount: acc.totalDiscount + (stat.total_discount || 0),
    }), {
      totalClicks: 0,
      totalUniqueClicks: 0,
      totalRegistrations: 0,
      totalRedemptions: 0,
      totalUniqueUsers: 0,
      totalDiscount: 0,
    });
  };

  const getConversionRate = (registrations, clicks) => {
    if (clicks === 0) return 0;
    return Math.round((registrations / clicks) * 100);
  };

  const getStatusChip = (stat) => {
    if (stat.registrations > 0) {
      return <Chip label="Active" color="success" size="small" />;
    }
    if (stat.clicks > 0) {
      return <Chip label="Visited" color="warning" size="small" />;
    }
    return <Chip label="No Activity" color="default" size="small" />;
  };

  const handleExport = (format) => {
    // Implementation for exporting data
    console.log(`Exporting data in ${format} format`);
  };

  const totalStats = getTotalStats();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Referral Statistics
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            startIcon={<Download />}
            onClick={() => handleExport('csv')}
            variant="outlined"
          >
            Export CSV
          </Button>
          <Button
            startIcon={<Refresh />}
            onClick={loadStats}
            variant="contained"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Filters and Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  label="Time Period"
                >
                  {FILTER_OPTIONS.DATE_RANGES.map(range => (
                    <MenuItem key={range.value} value={range.value}>
                      {range.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Code Type</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  label="Code Type"
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {FILTER_OPTIONS.CODE_TYPE.map(type => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                placeholder="Search codes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredStats.length} of {stats.length} codes
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <Grid container spacing={3} mb={3}>
        {!isChampions && (
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Visibility sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h4" component="div">
                  {totalStats.totalClicks.toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Clicks
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <People sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h4" component="div">
                {totalStats.totalUniqueClicks.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unique Clicks
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" component="div">
                {totalStats.totalRegistrations.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Registrations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" component="div">
                {getConversionRate(totalStats.totalRegistrations, totalStats.totalClicks)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Conversion Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {!isChampions && (
          <>
            <Grid item xs={12} sm={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AttachMoney sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                  <Typography variant="h4" component="div">
                    {totalStats.totalRedemptions.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Redemptions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AttachMoney sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                  <Typography variant="h4" component="div">
                    ${totalStats.totalDiscount.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Discount
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Error Alert - not shown for champions */}
      {error && !isChampions && (
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadStats}>
            Retry
          </Button>
        } sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Detailed Stats Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detailed Statistics ({filteredStats.length} codes)
          </Typography>
          
          {filteredStats.length === 0 ? (
            <Alert severity="info">
              {MESSAGES.INFO.NO_DATA}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Clicks</TableCell>
                    <TableCell>Unique Clicks</TableCell>
                    <TableCell>Registrations</TableCell>
                    {!isChampions && <TableCell>Redemptions</TableCell>}
                    <TableCell>Unique Users</TableCell>
                    {!isChampions && <TableCell>Total Discount</TableCell>}
                    <TableCell>Conversion Rate</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStats.map((stat) => (
                    <TableRow key={stat.code}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {stat.code}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Chip 
                          label={stat.code_type} 
                          color={stat.code_type === 'REFERRAL' ? 'primary' : 'secondary'}
                          size="small"
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {stat.clicks.toLocaleString()}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {stat.unique_clicks.toLocaleString()}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2" color="success.main">
                          {stat.registrations.toLocaleString()}
                        </Typography>
                      </TableCell>
                      
                      {!isChampions && (
                        <TableCell>
                          <Typography variant="body2">
                            {stat.redemptions.toLocaleString()}
                          </Typography>
                        </TableCell>
                      )}
                      
                      <TableCell>
                        <Typography variant="body2">
                          {stat.unique_users.toLocaleString()}
                        </Typography>
                      </TableCell>
                      
                      {!isChampions && (
                        <TableCell>
                          <Typography variant="body2" color="primary.main">
                            ${(stat.total_discount || 0).toFixed(2)}
                          </Typography>
                        </TableCell>
                      )}
                      
                      <TableCell>
                        <Typography variant="body2">
                          {getConversionRate(stat.registrations, stat.clicks)}%
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        {getStatusChip(stat)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdminReferralStats;
